type EventSourcePostMessageEvent = {
  data: string;
};

// EventSource doesn't support POST requests, so we need to use a custom implementation.
// Changing the endpoint to use GET requests is not a great idea because the endpoint performs work that is not pre-fetchable.
// OTOH, creating a POST endpoint that returns a session ID and then doing a GET with it would introduce a background job + complexity.
export class EventSourcePost {
  public onmessage: ((event: EventSourcePostMessageEvent) => void) | null = null;
  public onerror: ((error: unknown) => void) | null = null;
  public onclose: (() => void) | null = null;

  private readonly abortController = new AbortController();
  private closed = false;

  public constructor(private readonly url: string, private readonly requestInit: Omit<RequestInit, 'signal'>) {
    void this.connect();
  }

  public close() {
    if (this.closed) return;

    this.closed = true;
    this.abortController.abort();
    this.onclose?.();
  }

  private async connect() {
    try {
      const response = await fetch(this.url, {
        ...this.requestInit,
        signal: this.abortController.signal,
      });
      if (!response.ok) {
        // Surface the HTTP status on the error so the consumer's onerror can
        // branch on it (e.g. a 401 should open the unified session modal, not
        // show a generic connection-lost message). This stays a dumb transport.
        const error = new Error(`HTTP ${response.status}`) as Error & { status?: number };
        error.status = response.status;
        throw error;
      }
      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!this.closed) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() ?? '';

        for (const eventChunk of events) {
          const dataLines = eventChunk
            .split(/\r?\n/)
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.slice('data:'.length).trimStart());

          if (!dataLines.length) continue;

          this.onmessage?.({ data: dataLines.join('\n') });
        }
      }
    } catch (error) {
      if (!this.closed) {
        this.onerror?.(error);
      }
    } finally {
      this.close();
    }
  }
}
